import { Paper, Box, TextField, Button, CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AppRoute } from '../../routes';
import { ContentContainer } from '../ContentContainer';
import { GenericToolbar } from '../GenericToolbar';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useSelectedOrganizationData, isRecordFound, useRefreshLocationList } from '../../hooks/data-hooks';
import { EntityType, isDataLoaded, SiteRegistration } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { Actions, Thunks } from '../../actions';
import { useAuthHelper } from '../AuthContextProvider';
import { isLatitude, isLongitude, isValidCharWithoutSpace } from '../../lib/site-helper';
import locationStyles from '../../app/location-management/LocationStyle';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { DECIMAL_NUMBER } from '../../constants';

import { HeaderInfo } from '../HeaderInfo/HeaderInfo';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';

export const AddSite: React.FC<{ testId: string; appRoute: AppRoute }> = (props) => {
    const history = useHistory();
    const { t } = useLanguageLocale();
    const classes = locationStyles();
    const currentAppRoute = useCurrentAppRoute();
    const selectedOrganization = useSelectedOrganizationData();
    const locationList = useRefreshLocationList();
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const userData = useSelector(Selectors.User.user);
    const siteState = useSelector(Selectors.Site.site);

    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
    const locationId = 'locationId' in currentAppRoute ? currentAppRoute.locationId : '';

    const [siteInfo, setSiteInfo] = useState<SiteRegistration | null>(null);
    const [locationName, setLocationName] = useState('');
    const [unmodifiedSiteInfo, setUnmodifiedSiteInfo] = useState<SiteRegistration | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [additionalSiteCreated, setAdditionalSiteCreated] = useState(false);

    useEffect(() => {
        if (isDataLoaded(locationList)) {
            const locationObj = locationList.value.locations.find((x) => x.locationId === locationId);
            setLocationName(locationObj ? locationObj.locationName : '');
        }
    }, [locationList, locationId]);

    const initialValue = useCallback(() => {
        const siteData: SiteRegistration = {
            type: EntityType.Site,
            name: '',
            description: '',
            contact: `${userData.loggedInUser.user.fName} ${userData.loggedInUser.user.lName} ` || '',
            email: userData.loggedInUser.user.email || '',
            phone: userData.loggedInUser.user.phone || '',
            parentSiteId: locationId,
            siteAttributes: {
                address: '',
                city: '',
                country: '',
                state: '',
                zipCode: '',
                latitude: '',
                longitude: '',
            },
        };
        setUnmodifiedSiteInfo(siteData);
        setSiteInfo(siteData);
    }, [userData, locationId]);

    useEffect(() => {
        initialValue();
    }, [userData, locationId, locationName]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedSiteInfo) !== JSON.stringify(siteInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedSiteInfo, siteInfo]);

    useEffect(() => {
        if (!siteState.loading && siteState.success) {
            addToast('Site added successfully.');
            dispatch(Actions.Site.SiteRegistrationReset());
            dispatch(Thunks.Dashboard.locationRefresh(true));
            setIsDirty(false);

            if (siteState.success && siteState.siteId !== '' && !additionalSiteCreated) {
                pushAppRoute(history, {
                    type: 'SiteProfile',
                    orgId: orgId,
                    locationId: locationId,
                    siteId: siteState.siteId,
                });
            }
            if (additionalSiteCreated) initialValue();

            setAdditionalSiteCreated(false);
        }
        if (siteState.error) {
            dispatch(Actions.Site.SiteRegistrationReset());
        }
    }, [siteState]); //eslint-disable-line react-hooks/exhaustive-deps

    const createSite = (): void => {
        if (siteInfo) {
            dispatch(Thunks.Site.createSiteRegistration(siteInfo, addToast, authHelper));
        }
    };
    const addNewSite = (): void => {
        setAdditionalSiteCreated(true);
        createSite();
    };

    const onSubmit = (): void => {
        createSite();
    };

    const isDisable = (): boolean => {
        if (siteInfo)
            return (
                Boolean(siteInfo.name !== null && siteInfo.name.trimEnd() === '') ||
                isLatitude(siteInfo.siteAttributes.latitude) ||
                isLongitude(siteInfo.siteAttributes.longitude)
            );
        return false;
    };

    const onChangeSiteName = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedSiteName: SiteRegistration
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const siteNameObj: SiteRegistration = { ...modifiedSiteName };
            siteNameObj.name = e.target.value;
            setSiteInfo(siteNameObj);
        }
    };
    const onChangeNotes = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedNotes: SiteRegistration
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const notesObj: SiteRegistration = { ...modifiedNotes };
            notesObj.description = e.target.value;
            setSiteInfo(notesObj);
        }
    };
    const onChangeLatitude = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedLatitude: SiteRegistration
    ): void => {
        const value = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(value) || value === '') {
            const latitudeObj: SiteRegistration = { ...modifiedLatitude };
            latitudeObj.siteAttributes.latitude = value;
            setSiteInfo(latitudeObj);
        }
    };
    const onChangeLongitude = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedLongitude: SiteRegistration
    ): void => {
        const value = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(value) || value === '') {
            const longitudeObj: SiteRegistration = { ...modifiedLongitude };
            longitudeObj.siteAttributes.longitude = value;
            setSiteInfo(longitudeObj);
        }
    };
    const onSiteCancel = (): void => {
        pushAppRoute(history, props.appRoute);
    };

    return (
        <>
            <GenericToolbar
                showBackButton
                backButtonOnClick={(): void => {
                    pushAppRoute(history, props.appRoute);
                }}
                title="Add a Site "
                subtitle={`${locationName}`}
            />
            {siteInfo && (
                <ContentContainer>
                    <Paper style={{ padding: 48 }}>
                        <HeaderInfo title="Site Information" entity="to add a Site" />

                        <Box display="flex" flexDirection="column" alignItems="left">
                            <Box className={classes.padding8}>
                                <TextField
                                    inputProps={{
                                        'data-testid': 'name',
                                        maxLength: 100,
                                    }}
                                    style={{ width: '100%' }}
                                    required
                                    variant="filled"
                                    id="site-name"
                                    label="Site Name"
                                    value={siteInfo.name}
                                    onChange={(e): void => {
                                        onChangeSiteName(e, siteInfo);
                                    }}
                                    helperText={
                                        <Box component="span" display="flex" justifyContent="flex-end">
                                            {siteInfo.name.length}/100
                                        </Box>
                                    }
                                />
                            </Box>
                            <Box className={`${classes.padding8} ${classes.paddingBottom24}`}>
                                <TextField
                                    inputProps={{ 'data-testid': 'latitude' }}
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    error={isLatitude(siteInfo.siteAttributes.latitude)}
                                    helperText={isLatitude(siteInfo.siteAttributes.latitude) ? 'Invalid Latitude' : ''}
                                    id="Latitude"
                                    label="GPS Latitude"
                                    value={siteInfo.siteAttributes.latitude}
                                    onChange={(e): void => {
                                        onChangeLatitude(e, siteInfo);
                                    }}
                                />
                            </Box>
                            <Box className={`${classes.padding8} ${classes.paddingBottom24}`}>
                                <TextField
                                    inputProps={{ 'data-testid': 'longitude' }}
                                    style={{ width: '100%' }}
                                    error={isLongitude(siteInfo.siteAttributes.longitude)}
                                    helperText={
                                        isLongitude(siteInfo.siteAttributes.longitude) ? 'Invalid Longitude' : ''
                                    }
                                    id="longitude"
                                    variant="filled"
                                    label="GPS Longitude"
                                    value={siteInfo.siteAttributes.longitude}
                                    onChange={(e): void => {
                                        onChangeLongitude(e, siteInfo);
                                    }}
                                />
                            </Box>
                            <Box className={`${classes.padding8} `}>
                                <TextField
                                    inputProps={{ 'data-testid': 'notes', maxLength: 255 }}
                                    style={{ width: '100%' }}
                                    id="Notes"
                                    multiline
                                    maxRows={Infinity}
                                    label="Notes"
                                    variant="filled"
                                    value={siteInfo.description}
                                    onChange={(e): void => {
                                        onChangeNotes(e, siteInfo);
                                    }}
                                    helperText={
                                        <Box component="span" display="flex" justifyContent="flex-end">
                                            {siteInfo.description.length}/255
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                        <Box className={classes.addButton}>
                            <Button
                                style={{ marginRight: 16 }}
                                variant="outlined"
                                color="primary"
                                data-testid="cancel-location"
                                onClick={onSiteCancel}
                            >
                                {t('ACTIONS.CANCEL')}
                            </Button>
                            <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    style={{ marginRight: 24 }}
                                    variant="outlined"
                                    disabled={!siteState.loading ? isDisable() : true}
                                    startIcon={
                                        siteState.loading && additionalSiteCreated ? (
                                            <CircularProgress color="primary" size="1rem" />
                                        ) : (
                                            <></>
                                        )
                                    }
                                    color="primary"
                                    data-testid="additional-site"
                                    onClick={addNewSite}
                                >
                                    Save and Add Additional Site
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!siteState.loading ? isDisable() : true}
                                    startIcon={
                                        siteState.loading && !additionalSiteCreated ? (
                                            <CircularProgress size="1rem" style={{ color: 'white' }} />
                                        ) : (
                                            <></>
                                        )
                                    }
                                    color="primary"
                                    data-testid="add-site-button"
                                    onClick={onSubmit}
                                >
                                    {t('ACTIONS.SUBMIT')}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </ContentContainer>
            )}
            {isDirty && !siteState.success && <NavigationPromptModal isDirty={isDirty} />}
        </>
    );
};
